<script setup lang="ts">
  import { computed } from 'vue';

  import { t } from '../../locale';

  export interface LabelProps {
    /**
     * The `id` of the input element that this label is associated with.
     */
    for?: string;

    /**
     * Indicates whether the label should be styled as an error.
     */
    hasError?: boolean;

    /**
     * Indicates whether the input element that this label is associated with is required.
     */
    isRequired?: boolean;

    /**
     * Indicates whether the label should show that the input is optional.
     */
    showOptional?: boolean;

    /**
     * Indicates whether the wrapper element to be rendered should be a label or a legend.
     */
    legend?: boolean;

    /**
     * Indicates whether the label is disabled.
     */
    disabled?: boolean;

    /**
     * Indicates whether the label should be visually hidden (screen reader only).
     */
    isSrOnly?: boolean;
  }

  const props = withDefaults(defineProps<LabelProps>(), {
    for: undefined,
    hasError: false,
    isRequired: false,
    showOptional: false,
    legend: false,
    isDisabled: false,
    isSrOnly: false,
  });

  const is = computed(() => {
    return props.legend ? 'legend' : 'label';
  });
</script>

<template>
  <component
    :is="is"
    class="stash-label block truncate text-sm font-medium leading-6"
    :class="{
      'animate-shake': props.hasError,
      'stash-label--disabled text-ice-700': props.disabled,
      'text-ice-900': !props.disabled,
      'sr-only': props.isSrOnly,
    }"
    :for="props.for"
    data-test="stash-label"
  >
    <!-- @slot The label text -->
    <slot></slot>
    <span v-if="props.isRequired" class="font-semibold text-red-500" title="Required" aria-label="required"> * </span>
    <span v-else-if="props.showOptional" class="text-ice-700"> ({{ t('ll.optional') }})</span>
  </component>
</template>
